import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { designerAPI, reviewsAPI } from '../utils/api';
import { getTheme, PATTERNS } from '../utils/themes';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, generateWhatsAppLink } from '../utils/helpers';
import { MapPin, WhatsappLogo, Star, Phone, Eye, InstagramLogo, FacebookLogo, YoutubeLogo, TwitterLogo, LinkedinLogo, Globe } from '@phosphor-icons/react';

export const PublicProfilePage = () => {
  const { designerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme('modern_orange'));

  const [leadForm, setLeadForm] = useState({
    customer_name: '',
    mobile: '',
    email: '',
    message: '',
  });

  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    rating: 5,
    comment: '',
  });

  useEffect(() => {
    fetchPublicProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designerId]);

  const fetchPublicProfile = async () => {
    try {
      const response = await designerAPI.getPublicProfile(designerId);
      setData(response.data);
      // Set theme based on designer's preference
      if (response.data?.designer?.theme) {
        setTheme(getTheme(response.data.designer.theme));
      }
    } catch (error) {
      toast.error('Designer not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        designer_id: designerId,
        ...leadForm,
        source: 'profile',
      };
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      toast.success('Your request has been sent!');
      setDialogOpen(false);
      setLeadForm({ customer_name: '', mobile: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send request');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      await reviewsAPI.create({
        designer_id: designerId,
        ...reviewForm,
      });
      toast.success('Thank you for your review!');
      fetchPublicProfile();
      setReviewForm({ customer_name: '', rating: 5, comment: '' });
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const contactWhatsApp = () => {
    if (data?.designer?.whatsapp) {
      const message = `Hi, I found your profile and would like to discuss a project.`;
      window.open(generateWhatsAppLink(data.designer.whatsapp, message), '_blank');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-[#6B7280]">Designer profile not found</p>
      </div>
    );
  }

  const { designer, services, portfolio, reviews } = data;
  const avgRating = reviews.length > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const colors = theme.colors;
  const isDark = colors.isDark;
  const pattern = PATTERNS[theme.pattern];

  return (
    <div 
      className={`min-h-screen bg-gradient-to-br ${colors.background}`} 
      style={{ backgroundImage: `url("${pattern}")` }}
      data-testid="public-profile-page"
    >
      {/* Hero Header Section */}
      <div className={`bg-gradient-to-r ${colors.headerBg} relative overflow-hidden`}>
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
          <div className="text-center">
            {designer.photo_url && (
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div 
                    className="absolute inset-0 rounded-full animate-pulse"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, transform: 'scale(1.1)', opacity: 0.5 }}
                  ></div>
                  <img 
                    src={designer.photo_url} 
                    alt={designer.name}
                    className="w-36 h-36 rounded-full object-cover border-4 border-white shadow-2xl relative z-10"
                  />
                </div>
              </div>
            )}
            <h1 className="font-['Chivo'] text-5xl md:text-6xl font-black tracking-tighter text-white mb-4 drop-shadow-lg">
              {designer.name}
            </h1>
            {designer.location && (
              <div className="flex items-center justify-center gap-2 text-white/90 mb-4">
                <MapPin className="h-5 w-5" weight="duotone" />
                <span className="font-medium">{designer.location}</span>
              </div>
            )}
            {designer.bio && (
              <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-6">
                {designer.bio}
              </p>
            )}

            {/* Social Media Icons */}
            {(designer.instagram || designer.facebook || designer.youtube || designer.twitter || designer.linkedin || designer.website) && (
              <div className="flex items-center justify-center gap-3 mb-8">
                {designer.instagram && (
                  <a 
                    href={designer.instagram.startsWith('http') ? designer.instagram : `https://${designer.instagram}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="Instagram"
                  >
                    <InstagramLogo className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
                {designer.facebook && (
                  <a 
                    href={designer.facebook.startsWith('http') ? designer.facebook : `https://${designer.facebook}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="Facebook"
                  >
                    <FacebookLogo className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
                {designer.youtube && (
                  <a 
                    href={designer.youtube.startsWith('http') ? designer.youtube : `https://${designer.youtube}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="YouTube"
                  >
                    <YoutubeLogo className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
                {designer.twitter && (
                  <a 
                    href={designer.twitter.startsWith('http') ? designer.twitter : `https://${designer.twitter}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="Twitter/X"
                  >
                    <TwitterLogo className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
                {designer.linkedin && (
                  <a 
                    href={designer.linkedin.startsWith('http') ? designer.linkedin : `https://${designer.linkedin}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="LinkedIn"
                  >
                    <LinkedinLogo className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
                {designer.website && (
                  <a 
                    href={designer.website.startsWith('http') ? designer.website : `https://${designer.website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                    title="Website"
                  >
                    <Globe className="h-6 w-6 text-white" weight="fill" />
                  </a>
                )}
              </div>
            )}
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {designer.whatsapp && (
                <Button
                  data-testid="whatsapp-contact-button"
                  onClick={contactWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8 py-6 text-lg shadow-lg transform hover:scale-105 transition-all"
                >
                  <WhatsappLogo className="h-6 w-6 mr-2" weight="fill" /> WhatsApp वर संपर्क करा
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="request-quote-button" 
                    className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8 py-6 text-lg shadow-lg transform hover:scale-105 transition-all"
                  >
                    Quote मागा
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="request-quote-dialog" className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                  <DialogHeader>
                    <DialogTitle className={isDark ? 'text-white' : ''}>Quote साठी विनंती</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitLead} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className={isDark ? 'text-gray-200' : ''}>तुमचे नाव</Label>
                      <Input id="name" data-testid="lead-name-input" value={leadForm.customer_name} onChange={(e) => setLeadForm({ ...leadForm, customer_name: e.target.value })} required className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mobile" className={isDark ? 'text-gray-200' : ''}>मोबाइल नंबर</Label>
                      <Input id="mobile" data-testid="lead-mobile-input" value={leadForm.mobile} onChange={(e) => setLeadForm({ ...leadForm, mobile: e.target.value })} required className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className={isDark ? 'text-gray-200' : ''}>Email (Optional)</Label>
                      <Input id="email" data-testid="lead-email-input" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className={isDark ? 'text-gray-200' : ''}>प्रोजेक्ट डिटेल्स</Label>
                      <Textarea id="message" data-testid="lead-message-input" value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })} placeholder="तुमच्या प्रोजेक्ट बद्दल सांगा..." className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} />
                    </div>
                    <Button type="submit" data-testid="submit-lead-button" className={`w-full ${colors.button} text-white rounded-full`}>विनंती पाठवा</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Rating & Visit Count Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              {/* Visit Count Badge */}
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2">
                <Eye className="h-5 w-5 text-white" weight="fill" />
                <span className="text-white font-bold">{designer.visit_count || 0}</span>
                <span className="text-white/80">profile views</span>
              </div>
              
              {/* Rating Badge */}
              {reviews.length > 0 && (
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-5 w-5 ${i < Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-white/40'}`} weight="fill" />
                    ))}
                  </div>
                  <span className="text-white font-bold">{avgRating}</span>
                  <span className="text-white/80">({reviews.length} reviews)</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      {services.length > 0 && (
        <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 ${isDark ? 'text-white' : ''}`} data-testid="services-section">
          <h2 className={`font-['Chivo'] text-3xl font-black tracking-tighter mb-8 ${isDark ? 'text-white' : 'text-[#121212]'}`}>
            <span className={colors.text}>सेवा</span> आणि दर
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card 
                key={service.id} 
                data-testid={`service-card-${service.id}`} 
                className={`${isDark ? 'bg-gray-800/50 backdrop-blur-sm border-gray-700' : 'bg-white/80 backdrop-blur-sm'} ${colors.cardBorder} ${colors.cardHover} transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
              >
                <CardContent className="pt-6">
                  <div 
                    className="w-12 h-12 rounded-lg mb-4 flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}
                  >
                    <span className="text-white text-xl font-bold">{service.name.charAt(0)}</span>
                  </div>
                  <h3 className={`font-['Chivo'] text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-[#121212]'}`}>{service.name}</h3>
                  {service.description && <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-[#6B7280]'}`}>{service.description}</p>}
                  <div className="flex items-baseline gap-1">
                    <span className={`text-3xl font-black ${colors.text}`}>{formatCurrency(service.price)}</span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-[#6B7280]'}`}>/ {service.unit}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Portfolio Section */}
      {portfolio.length > 0 && (
        <div className={`py-16 ${isDark ? 'bg-gray-800/30' : 'bg-white/50'}`} data-testid="portfolio-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`font-['Chivo'] text-3xl font-black tracking-tighter mb-8 ${isDark ? 'text-white' : 'text-[#121212]'}`}>
              <span className={colors.text}>माझे</span> काम
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolio.map((item) => (
                <div 
                  key={item.id} 
                  data-testid={`portfolio-item-${item.id}`} 
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <img 
                    src={item.url} 
                    alt={item.title || 'Portfolio item'} 
                    className="w-full h-72 object-cover transform group-hover:scale-110 transition-transform duration-500" 
                  />
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4"
                    style={{ background: `linear-gradient(to top, ${colors.primaryDark}ee, transparent)` }}
                  >
                    {item.title && <p className="text-white font-bold text-lg">{item.title}</p>}
                    {item.category && (
                      <span className={`inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full ${colors.badge}`}>
                        {item.category}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reviews Section */}
      <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16`} data-testid="reviews-section">
        <h2 className={`font-['Chivo'] text-3xl font-black tracking-tighter mb-8 ${isDark ? 'text-white' : 'text-[#121212]'}`}>
          <span className={colors.text}>Customer</span> Reviews
        </h2>
        
        {/* Add Review Card */}
        <Card 
          className={`${isDark ? 'bg-gray-800/50 backdrop-blur-sm border-gray-700' : 'bg-white/80 backdrop-blur-sm'} ${colors.cardBorder} mb-8`} 
          data-testid="add-review-card"
        >
          <CardContent className="pt-6">
            <h3 className={`font-medium mb-4 ${isDark ? 'text-white' : 'text-[#121212]'}`}>तुमचा Review द्या</h3>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="review-name" className={isDark ? 'text-gray-200' : ''}>तुमचे नाव</Label>
                <Input 
                  id="review-name" 
                  data-testid="review-name-input" 
                  value={reviewForm.customer_name} 
                  onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })} 
                  required 
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating" className={isDark ? 'text-gray-200' : ''}>Rating</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      data-testid={`star-rating-${star}`}
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      className="focus:outline-none transform hover:scale-110 transition-transform"
                    >
                      <Star className={`h-10 w-10 ${star <= reviewForm.rating ? 'text-yellow-500 fill-yellow-500' : isDark ? 'text-gray-600' : 'text-gray-300'}`} weight="fill" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="comment" className={isDark ? 'text-gray-200' : ''}>तुमचा अनुभव</Label>
                <Textarea 
                  id="comment" 
                  data-testid="review-comment-input" 
                  value={reviewForm.comment} 
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} 
                  placeholder="तुमचा अनुभव शेअर करा..." 
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                />
              </div>
              <Button type="submit" data-testid="submit-review-button" className={`${colors.button} text-white rounded-full px-8`}>
                Review Submit करा
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Reviews List */}
        <div className="space-y-4">
          {reviews.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-800/30' : 'bg-white/50'}`}>
              <Star className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} weight="duotone" />
              <p className={`${isDark ? 'text-gray-400' : 'text-[#6B7280]'}`}>अजून कोणताही review नाही. पहिला review द्या!</p>
            </div>
          ) : (
            reviews.map((review) => (
              <Card 
                key={review.id} 
                data-testid={`review-item-${review.id}`} 
                className={`${isDark ? 'bg-gray-800/50 backdrop-blur-sm border-gray-700' : 'bg-white/80 backdrop-blur-sm'} ${colors.cardBorder}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}
                    >
                      {review.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-[#121212]'}`}>{review.customer_name}</h4>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : isDark ? 'text-gray-600' : 'text-gray-300'}`} weight="fill" />
                          ))}
                        </div>
                      </div>
                      {review.comment && <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-[#6B7280]'}`}>{review.comment}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Bottom CTA Section - Create Your Profile (Only show if designer is not premium) */}
      {!designer.is_premium && (
        <div className="bg-gradient-to-r from-[#FF5A36] via-[#E04828] to-orange-600 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="font-['Chivo'] text-2xl md:text-4xl font-black text-white mb-4">
              तुमची Designer Profile बनवा FREE! 🎨
            </h3>
            <p className="text-white/90 text-lg mb-6">
              आपलं portfolio online करा आणि नवीन clients मिळवा
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Button
                onClick={() => window.location.href = '/auth'}
                className="bg-white hover:bg-gray-100 text-[#FF5A36] rounded-full px-8 py-6 text-lg font-black shadow-xl hover:scale-105 transition-all"
                data-testid="cta-create-profile-btn"
              >
                FREE Profile बनवा →
              </Button>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 inline-block">
              <p className="text-white text-sm mb-2">
                <span className="font-bold">Premium Plan:</span> ₹99/month
              </p>
              <p className="text-white/80 text-xs">
                Enquiry Management • Invoices • Payment Tracking • Analytics
              </p>
            </div>
            <p className="text-white/70 text-xs mt-4">
              Powered by <span className="font-bold">Designbiz.in</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfilePage;